import { type ExtractedDate, type ExtractedPrazo } from '../../api/client';

interface HighlightRegion {
  posicao: number;
  length: number;
  kind: 'date' | 'prazo' | 'ato';
  tooltip?: string;
  data?: ExtractedDate;
}

interface HighlightedTextProps {
  texto: string;
  datas: ExtractedDate[];
  prazos?: ExtractedPrazo[];
  /** O trecho que decidiu o ato, quando o prazo veio da lei (prazo_presumido.trecho). */
  trechoDoAto?: { posicao: number; tamanho: number } | null;
  /** Datas ISO (YYYY-MM-DD) já na agenda — clique navega para calendário. */
  scheduledDates?: Set<string>;
  onDateClick?: (date: ExtractedDate) => void;
  onScheduledDateClick?: (date: ExtractedDate) => void;
}

// A data em si, a partir da posição: "10/11/2026" ou "10 de novembro de 2026".
// O `contexto` da extração é o trecho em volta (50 antes, 30 depois) — usado
// como tamanho, o destaque engolia o prazo citado logo depois da data, que
// ficava sem âncora e o chip "ver no texto" não achava.
const DATA_NO_TEXTO = /^(?:\d{1,2}[./-]\d{1,2}[./-]\d{2,4}|\d{1,2}º?\s+de\s+[a-zç]+\s+de\s+\d{4})/i;

function tamanhoDaData(texto: string, posicao: number): number {
  return DATA_NO_TEXTO.exec(texto.slice(posicao, posicao + 40))?.[0].length ?? 10;
}

const UNIDADE_NO_TOOLTIP: Record<string, [string, string]> = {
  horas: ['hora', 'horas'],
  meses: ['mês', 'meses'],
  anos: ['ano', 'anos'],
};

const HIGHLIGHT_STYLES = {
  date: 'bg-yellow-200/60 dark:bg-yellow-500/20 hover:bg-yellow-300/80 dark:hover:bg-yellow-500/30 cursor-pointer',
  prazo: 'bg-yellow-200/60 dark:bg-yellow-500/20',
  ato: 'bg-yellow-200/60 dark:bg-yellow-500/20',
} as const;

/**
 * Renderiza texto com datas e prazos extraídos destacados.
 * Datas de audiência: fundo amarelo, clicáveis.
 * Prazos: fundo azul sutil, não clicáveis.
 */
export function HighlightedText({ texto, datas, prazos, trechoDoAto, scheduledDates, onDateClick, onScheduledDateClick }: HighlightedTextProps) {
  if ((!datas || datas.length === 0) && (!prazos || prazos.length === 0) && !trechoDoAto) {
    return <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{texto}</p>;
  }

  // Montar lista unificada de regiões
  const regions: HighlightRegion[] = [];

  for (const d of datas) {
    regions.push({
      posicao: d.posicao,
      length: tamanhoDaData(texto, d.posicao),
      kind: 'date',
      data: d,
    });
  }

  for (const p of prazos ?? []) {
    if (!p.tamanho) continue;
    regions.push({
      posicao: p.posicao,
      length: p.tamanho,
      kind: 'prazo',
      tooltip: `Prazo: ${p.dias} ${(UNIDADE_NO_TOOLTIP[p.unidade] ?? ['dia', 'dias'])[p.dias === 1 ? 0 : 1]}`,
    });
  }

  if (trechoDoAto && trechoDoAto.tamanho > 0) {
    regions.push({
      posicao: trechoDoAto.posicao,
      length: trechoDoAto.tamanho,
      kind: 'ato',
      tooltip: 'O ato que dá o prazo da lei',
    });
  }

  // Ordenar por posição; resolver empates priorizando datas (são clicáveis)
  regions.sort((a, b) => a.posicao - b.posicao || (a.kind === 'date' ? -1 : 1));

  const segments: React.ReactNode[] = [];
  let lastEnd = 0;

  for (const r of regions) {
    const start = r.posicao;
    const end = start + r.length;

    // Evitar sobreposição
    if (start < lastEnd) continue;

    // Texto antes da região
    if (start > lastEnd) {
      segments.push(
        <span key={`txt-${lastEnd}`}>{texto.slice(lastEnd, start)}</span>,
      );
    }

    // Span destacado
    segments.push(
      <span
        key={`hl-${start}`}
        id={r.kind === 'prazo' ? `prazo-${start}` : undefined}
        className={`rounded px-0.5 transition-colors ${HIGHLIGHT_STYLES[r.kind]}${r.kind === 'date' ? ' cursor-pointer' : ''}`}
        onClick={
          r.kind === 'date' && r.data
            ? scheduledDates?.has(r.data.data)
              ? () => onScheduledDateClick?.(r.data!)
              : () => onDateClick?.(r.data!)
            : undefined
        }
        title={r.tooltip ?? undefined}
      >
        {texto.slice(start, end)}
      </span>,
    );

    lastEnd = end;
  }

  // Texto restante
  if (lastEnd < texto.length) {
    segments.push(
      <span key={`txt-${lastEnd}`}>{texto.slice(lastEnd)}</span>,
    );
  }

  return <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{segments}</p>;
}
